import intersect from 'intersect';
import consolv from 'lib/consolv';
import queryString from 'query-string';
import waitForDependency from 'lib/waitForDependency';

const defaultConfig = {
	enabled: false,
	enableNCA: false,
	publisherUUID: '',
	pubID: '',
	timeout: 1000,
	failsafeTimeout: 2000,
	deals: false,
	includeTokens: true,
	revokeTokens: false,
	tokenConfig: {
		optOut: false,
		duration: 1209600,
		gdpr: {
			enabled: false,
		},
	},
};

// Promise that resolves to apstag reference when loaded
let waitForApstag;

function lookForTokens(a9Config) {
	const defaults = defaultConfig.tokenConfig;
	const config = a9Config.tokenConfig || defaults;
	const queryStringParams = queryString.parse(window.location.search);
	const emailToken = queryStringParams.lvrmp;
	if (emailToken) {
		const tokenConfig = {
			hashedRecords: [
				{ type: 'email', record: emailToken },
			],
			gdpr: config.gdpr || defaults.gdpr,
			duration: config.duration || defaults.duration,
			optOut: config.optOut !== false ? config.optOut : defaults.optOut,
		};

		waitForApstag.then((apstag) => {
			apstag.rpa(tokenConfig);
		})
	}
}

function revokeTokens() {
	waitForApstag.then((apstag) => {
		apstag.dpa();
	})
}

// /* eslint-disable prefer-rest-params */
// function setupA9Globals() {
//	 if (window.apstag) return;

//	 function queue(command, args) {
//		 window.apstag._Q.push([command, args]);
//	 }

//	 window.apstag = {
//		 init(...args) {
// 			aps.then((apstag) => {
// 				console.log('init', apstag)
//				 apstag.init.apply(null, args);
// 			})
//		 },
//		 fetchBids(...args) {
// 			aps.then((apstag) => {
//				 apstag.fetchBids.apply(null, args);
// 			});
//		 },
//		 setDisplayBids() {},
//		 targetingKeys() {
//			 return [];
//		 },
//		 dpa() {
//			 queue('di', arguments);
//		 },
//		 rpa() {
//			 queue('ri', arguments);
//		 },
//		 upa() {
//			 queue('ui', arguments);
//		 },
//		 _Q: [],
//	 };
// }
/* eslint-enable prefer-rest-params */

function setupA9GlobalsV2(accountId) {
	const aps = ((e = accountId) => {
		(window._aps = window._aps || new Map()),
			_aps.has(e) ||
				_aps.set(e, { queue: [], store: new Map([["listeners", new Map()]]) });
		let t = {
			accountID: e,
			record: function (e, t) {
				return new Promise((r, n) => {
					_aps
						.get(this.accountID)
						.queue.push(
							new CustomEvent(e, { detail: { ...t, resolve: r, reject: n } })
						);
				});
			},
			read: function (e) {
				return (
					this.record("bootstrap/store/willRead", { name: e }),
					_aps.get(this.accountID).store.get(e)
				);
			},
			run: function (e, t) {
				_aps.get("_system")?.store.get("listeners").has(e)
					? this.record(e, t)
					: this.record("bootstrap/run/willDrop", { name: e, detail: t });
			},
			react: function (e, t) {
				_aps.get(this.accountID).store.get("listeners").set(e, t);
				this.record("bootstrap/react/didExecute", { name: e });
			},
		};
		return t.record("bootstrap/version/declare", { version: 5 }), t;
	})();

	((t = aps) => {
		window.apstag = window.apstag || {
			init: function (e, r) {
				t.record("legacy/init/enqueue", { config: e, callback: r });
			},
			fetchBids: function (e, r) {
				t.record("legacy/fetchBids/enqueue", { bidConfig: e, callback: r });
			},
			setDisplayBids: function () {
				t.run("legacy/setDisplayBids/enqueue", { arguments: arguments });
			},
			targetingKeys: function () {
				return t.read("legacy/targetingKeys") ?? [];
			},
			rpa: function (e, r, n) {
				t.record("legacy/rpa/enqueue", {
					tokenConfig: e,
					callback: r,
					setCookie: n,
				});
			},
			upa: function (e, r, n) {
				t.record("legacy/upa/enqueue", {
					tokenConfig: e,
					callback: r,
					setCookie: n,
				});
			},
			dpa: function (e) {
				t.record("legacy/dpa/enqueue", { callback: e });
			},
		};
		t.record("bootstrapExtAPStag/version/declare", { version: 1 });
	})();

	((t = aps) => {
		window.apstag = {
			...window.apstag,
			queue: {
				push: function (e) {
					t.record("legacy/queue/push", { callback: e });
				},
			},
		};
		t.record("bootstrapExtQueue/version/declare", { version: 1 });
	})();

	((t = aps) => {
		window.apstag = {
			...window.apstag,
			nativeContent: function () {
				return {
					enable: function () {
						t.record("nativeCommerceAds/service/enable");
					},
					registerHook: function (e, r) {
						if (e === "nativeCommerceAds.updateClickUrl") {
							t.react("nativeCommerceAds/clickUrl/update", (k) => {
								return {
									status: "completed",
									value: { url: r(k?.detail?.url, k?.detail?.element) },
								};
							});
						} else {
							throw new Error("Unsupported event type");
						}
					},
				};
			},
		};
		t.record("bootstrapExtNCA/version/declare", { version: 1 });
	})();

}

/**
 * Creates the Amazon A9 request and callback that is called before loading slots
 */
const setupRequestBids = (MCP, config) => (event, { slots, addAsyncEvent }) => {
	const runPerformanceTest = MCP._config.performanceTest === true;
	let startTime;
	if (runPerformanceTest) startTime = window.performance.now();

	const asyncEvent = new Promise((resolve) => {
		const { slots: slotMap = {}, failsafeTimeout } = config;
		let failsafeTimerRef;

		/* eslint-disable-next-line no-unused-vars */
		const adUnits = Array.from(slots).reduce((acc, [element, slot]) => {
			const slotType = slot.meta.bidAs || slot.type;

			const { blockAmazonA9, blockHeaderBidding, amazonA9Sizes, gumGumAdhesion } = slot.meta;
			if (blockAmazonA9 || blockHeaderBidding) return acc;

			const slotConfig = slotMap[slotType];
			const sizes = slot.sizeMap
				.filter(v => v !== 'fluid')
				.map((d => d.join(',')));
			// if (!slotConfig) return acc;
			if (!sizes.length) return acc;

			// Allows the additional ability to configure sizes in slot mapping definitions
			const biddableSizes = Array.isArray(amazonA9Sizes) ? amazonA9Sizes : slotConfig?.sizes;

			// Will only include sizes listed in A9 configuration, unless no size map is configured
			const validSizes = Array.isArray(biddableSizes) ? intersect(biddableSizes, sizes) : sizes;
			consolv.log('MCP: AmazonA9 Biddable Sizes', slotType, validSizes);

			if (validSizes.length) {
				const slotParams = {};
				if (gumGumAdhesion) {
					slotParams.ggProduct = 'inScreen';
					consolv.log('Enabling GumGum adhesion banner through TAM');
				}

				acc.push({
					slotID: slot.id,
					slotName: slot.getAdUnitPath(),
					sizes: validSizes.map(v => v.split(',').map(n => Number(n))),
					slotParams,
				});
			}
			return acc;
		}, []);

		if (!adUnits.length) {
			consolv.log('MCP: AmazonA9 has no valid ad units');
			resolve();
			return;
		}

		consolv.log('MCP: AmazonA9 bids ready', adUnits);

		/* eslint-disable-next-line prefer-const */
		failsafeTimerRef = window.setTimeout(() => {
			consolv.log(`MCP: AmazonA9 failsafe timeout exceeded at ${failsafeTimeout} seconds`);
			resolve();
		}, failsafeTimeout);
		
		waitForApstag.then((apstag) => {
			apstag.fetchBids({ slots: adUnits },
				(bids) => {
					apstag.setDisplayBids();
					window.clearTimeout(failsafeTimerRef);

					if (runPerformanceTest) {
						const requestDuration = (window.performance.now() - startTime) / 1000;
						consolv.log('MCP: AmazonA9 requests received', bids, `${requestDuration} seconds`);
					} else {
						consolv.log('MCP: AmazonA9 requests received', bids);
					}

					resolve();
				});

		})
		.catch((e) => {
			resolve();
		});
	});

	addAsyncEvent(asyncEvent);
};

/**
 * Generates the configuration and sets up event listeners for slot loads
 * @param {MCPInstance} MCP
 */
export default function AmazonA9Adapter(MCP) {
	// setupA9Globals();
	waitForApstag = waitForDependency(() => window.apstag?.debug ? window.apstag : undefined);
	
	MCP.loaded.then(() => {
		const { amazonA9 } =
			MCP.Configurator.getConfiguration({ defaults: { amazonA9: defaultConfig } });

		if (amazonA9.enabled) {
			const { taggroup1 = {}, ing = [] } = MCP.Configurator.meta.page;
			const keywords = [...ing, ...Object.values(taggroup1)];

			if (amazonA9.enableNCA && amazonA9.publisherUUID) {
				consolv.log('MCP: Amazon Native Commerce Ads Enabled');
				setupA9GlobalsV2(amazonA9.pubID);
			}

			waitForApstag = waitForApstag.then((apstag) => {
				consolv.log('MCP: Amazon A9 initializing');

				const config = {
					pubID: amazonA9.pubID,
					adServer: 'googletag',
					videoAdServer: 'freeWheel',
					bidTimeout: amazonA9.timeout,
					deals: amazonA9.deals,
					signals: { ortb2: { site: {} } },
				};

				if (keywords.length) {
					consolv.log('MCP: Amazon A9 setting keywords', keywords);
					config.signals.ortb2.site.keywords = keywords.join(',');
				}
				
				if (amazonA9.enableNCA) {
					config.signals.ortb2.site.page = `${window.location.hostname}${window.location.pathname}`;
					config.signals.ortb2.site.publisher = { id: amazonA9.publisherUUID };
				}

				apstag.init(config);
				
				if (amazonA9.enableNCA) apstag.nativeContent?.().enable?.();

				return apstag;
			});

			if (amazonA9.includeTokens) lookForTokens(amazonA9);
			if (amazonA9.revokeTokens) revokeTokens();

			const requestBids = setupRequestBids(MCP, amazonA9);

			const amazonA9Callback = (...params) => requestBids('beforeRefreshSlots', ...params);
			MCP.addEventListener('beforeRefreshSlots', amazonA9Callback);
			
			waitForApstag.catch((e) => {
				consolv.log('MCP: Amazon A9 library failed to load', e);
				MCP.removeEventListener('beforeRefreshSlots', amazonA9Callback);
			})
		}
	});
}
